import { storeToRefs } from 'pinia';
import { computed, defineComponent, onMounted } from 'vue';
import { Setting, Delete, DCaret, Edit } from '@element-plus/icons-vue';
import {
  geoPaymentMethodColumnsList,
  GeoPaymentMethodColumnProperties,
  GeoSinglePaymentMethodPartialUpdateParams,
  GeoPaymentMethodOperation,
  GeoPaymentMethodItem,
  GeoSinglePaymentMethodPartialUpdateParameterKeyModel,
  OrganicsAvailabilityData,
  OrganicsAvailabilityStatus,
  OrganicsAvailabilityTextType,
  GeoSinglePaymentMethodListItem,
  GeoMultiPaymentMethodListItem,
  GeoPaymentMethodTypes,
  GeoPaymentMethodWithIdList,
  GeoDropdownActionCommand,
} from '@/models/ps-management/ps-management.model';
import PsLimitsDetails from '@/layouts/ps-management-page/ps-limits-details/ps-limits-details.vue';
import PaymentMethodToggle
  from '@/layouts/ps-management-page/geo-payment-methods-list/geo-payment-method-toggle/geo-payment-method-toggle.vue';
import { useGeoPsManagementStore } from '@/store/ps-management/geo-payment-methods.store';
// eslint-disable-next-line import/no-named-as-default
import Sortable from 'sortablejs';
import { useDragListSorting } from '@/components/drag-sorting/drag-sorting.hook';
import { ElMessageBox } from 'element-plus';
import { usePaymentMethodsDepositLimitsStore } from '@/store/ps-management/payment-methods-deposit-limits.store';
import { usePermissionsStore } from '@/store/permissions.store';
import { PAYMENTS_SECTIONS_ROUTES, PS_MANAGEMENT_PERMISSIONS } from '@/models/app-internal/permissions.model';
import router from '@/router/router';
import { GeoMultiMethodItem } from '@/models/ps-management/multi-methods.model';
import { TagComponentTypes } from '@/models/app-common.model';
import { useGeoMultiMethodsStore } from '@/store/ps-management/geo-multi-methods.store';
import { usePaymentMethodsExceptionLimitStore } from '@/store/ps-management/payment-method-exception-limits.store';
import { PaymentMethodExceptionLimitType } from '@/models/ps-management/exception-limit.model';
import FallbackImage from '@/components/fallback-image/fallback-image.vue';
import { usePaymentMethodsAvailabilityHoursStore } from '@/store/ps-management/availability-hours.store';
import {
  UnavailableHoursData,
  UnavailableHoursStatus,
  UnavailableHoursTextType,
} from '@/models/ps-management/availability-hours.model';

const icons = {
  Setting,
  Delete,
  DCaret,
  Edit,
};

export default defineComponent({
  name: 'GeoPaymentMethodsList',
  components: {
    PsLimitsDetails,
    PaymentMethodToggle,
    FallbackImage,
  },
  setup() {
    const geoPsManagementStore = useGeoPsManagementStore();
    const {
      partialUpdateGeoSinglePaymentMethod,
      deleteSingleGeoPaymentMethodById,
      openGeoPaymentMethodSettings,
      getGeoPaymentMethodList,
    } = geoPsManagementStore;
    const {
      geoPaymentMethodList,
      isGeoPaymentMethodListLoading,
      isWithdrawColumnsDataVisible,
      isDepositColumnsDataVisible,
    } = storeToRefs(geoPsManagementStore);

    const {
      updateGeoMultiMethodWithdrawState,
      deleteGeoMultiMethod,
    } = useGeoMultiMethodsStore();

    const { psManagementPermissions } = storeToRefs(usePermissionsStore());

    const isUpdateGeoPaymentMethodAllowed = computed(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD]);
    const isGetGeoPaymentMethodDepositLimitsAllowed = computed(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.GET_GEO_PAYMENT_METHOD_DEPOSIT_LIMITS]);
    const isUpdateGeoPaymentMethodDepositLimitsAllowed = computed(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD_DEPOSIT_LIMITS]);
    const isPartialUpdateGlobalPaymentMethodAllowed = computed(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD_DEPOSIT_AND_WITHDRAW]);
    const isDeleteGeoPaymentMethodAllowed = computed(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.DELETE_GEO_PAYMENT_METHOD]);
    const isGeoPaymentMethodFullListUpdateAllowed = computed(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD_LIST]);
    const isGetGeoPaymentMethodExtendedLimitAllowed = computed(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.GET_METHOD_EXTENDED_LIMIT_LIST]);
    const isGetGeoPaymentMethodSetAvailabilityLimitAllowed = computed(() =>
      psManagementPermissions.value[PS_MANAGEMENT_PERMISSIONS.GET_PAYMENT_METHOD_UNAVAILABLE_HOURS_SETTINGS]);

    const {
      dragSortingTableRef,
      dragOptions,
      isResortingInProgress,
    } = useDragListSorting();

    const paymentsDepositLimitsStore = usePaymentMethodsDepositLimitsStore();
    const { openPspLimitsSettings } = paymentsDepositLimitsStore;

    const { openExceptionLimitSettings } = usePaymentMethodsExceptionLimitStore();

    const { openAvailabilityHoursSettings } = usePaymentMethodsAvailabilityHoursStore();

    const updateSinglePaymentMethodSwitch = (payload: GeoSinglePaymentMethodPartialUpdateParams) => {
      partialUpdateGeoSinglePaymentMethod(payload);
    };

    const initDraggableTableSorting = () => {
      const geoPaymentMethodsTableElement = document.querySelector('.geo-payment-methods tbody') as HTMLElement;

      if (!geoPaymentMethodsTableElement) {
        return;
      }

      Sortable.create(geoPaymentMethodsTableElement, dragOptions);
    };

    onMounted(() => {
      initDraggableTableSorting();
    });

    const requestDeleteConfirmation = (geoPaymentMethod: GeoPaymentMethodItem | GeoMultiPaymentMethodListItem) => {
      ElMessageBox.confirm(
        'Are you sure want to delete this PS from cashier?',
        'Warning',
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: TagComponentTypes.WARNING,
        },
      )
        .then(() => {
          const methodDeleteActionsMapping = {
            [GeoPaymentMethodTypes.MULTI]: () =>
              deleteGeoMultiMethod((geoPaymentMethod as GeoMultiPaymentMethodListItem).multiMethodId),
            [GeoPaymentMethodTypes.SINGLE]: () =>
              deleteSingleGeoPaymentMethodById(geoPaymentMethod as GeoPaymentMethodItem),
          };

          return methodDeleteActionsMapping[geoPaymentMethod.type]().then(() => getGeoPaymentMethodList());
        });
    };

    const switchPaymentMethod = (
      currentRow: GeoSinglePaymentMethodListItem,
      currentColumn: GeoSinglePaymentMethodPartialUpdateParameterKeyModel,
    ) => {
      ElMessageBox.confirm(
        'Are you sure you want to toggle the PS for the cashier?',
        'Warning',
        {
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
          type: TagComponentTypes.WARNING,
        },
      )
        .then(() => {
          updateSinglePaymentMethodSwitch({
            parameterKey: currentColumn,
            billingPaymentMethodSettingsId: currentRow.billingPaymentMethodSettingsId,
            newValue: currentRow[currentColumn],
          });
        })
        .catch(() => {
          const geoPaymentMethod = geoPaymentMethodList.value
            .find((geoPaymentMethod) => {
              const geoPaymentMethodId = geoPaymentMethod.type === GeoPaymentMethodTypes.MULTI
                ? geoPaymentMethod.multiMethodId
                : geoPaymentMethod.billingPaymentMethodSettingsId;

              return geoPaymentMethodId === currentRow.billingPaymentMethodSettingsId;
            });

          if (!geoPaymentMethod) {
            return;
          }

          geoPaymentMethod[currentColumn] = !currentRow[currentColumn];
        });
    };

    const isActionsColumnVisible = computed(() =>
      isPartialUpdateGlobalPaymentMethodAllowed.value ||
      isUpdateGeoPaymentMethodAllowed.value ||
      isDeleteGeoPaymentMethodAllowed.value);

    const isSettingsButtonAllowed = computed<boolean>(() => {
      return (isUpdateGeoPaymentMethodAllowed.value ||
          isUpdateGeoPaymentMethodDepositLimitsAllowed.value);
    });

    const getOrganicsAvailabilityData = (
      geoPaymentMethod: GeoMultiPaymentMethodListItem | GeoSinglePaymentMethodListItem,
    ): OrganicsAvailabilityData => {
      const isOrganicsEnabled = geoPaymentMethod.type === GeoPaymentMethodTypes.MULTI
        ? geoPaymentMethod.organic
        : geoPaymentMethod.organicsEnabled;

      return isOrganicsEnabled
        ? {
            type: OrganicsAvailabilityTextType.SUCCESS,
            value: OrganicsAvailabilityStatus.AVAILABLE,
          }
        : {
            type: OrganicsAvailabilityTextType.DANGER,
            value: OrganicsAvailabilityStatus.UNAVAILABLE,
          };
    };

    const getAvailabilityHoursData = (isAvailable: boolean): UnavailableHoursData => {
      return isAvailable
        ? {
            type: UnavailableHoursTextType.SUCCESS,
            value: UnavailableHoursStatus.ACTIVE,
          }
        : {
            type: UnavailableHoursTextType.INFO,
            value: UnavailableHoursStatus.DISABLED,
          };
    };

    const getGeoDropdownActionCommand = (
      command: { action: GeoDropdownActionCommand; operationType?: PaymentMethodExceptionLimitType },
      row: GeoPaymentMethodItem,
    ) => {
      const { action, operationType } = command;

      const actionMap: Record<GeoDropdownActionCommand, () => void> = {
        [GeoDropdownActionCommand.OPEN_EDIT_PAYMENT_METHOD]: () =>
          openGeoPaymentMethodSettings(row.billingPaymentMethodSettingsId),
        [GeoDropdownActionCommand.OPEN_PSP_LIMITS_SETTINGS]: () =>
          openPspLimitsSettings(row.billingPaymentMethodSettingsId),
        [GeoDropdownActionCommand.OPEN_EXCEPTION_LIMIT_SETTINGS]: () =>
          openExceptionLimitSettings(row, operationType as PaymentMethodExceptionLimitType),
        [GeoDropdownActionCommand.OPEN_UNAVAILABLE_HOURS_SETTINGS]: () =>
          openAvailabilityHoursSettings(row),
      };

      actionMap[action]();
    };
    const getPaymentMethodTagType = (type: GeoPaymentMethodTypes): string => {
      return type === GeoPaymentMethodTypes.MULTI ? TagComponentTypes.PRIMARY : TagComponentTypes.WARNING;
    };

    const openMultiMethodPage = (multiMethodItem: GeoMultiMethodItem): void => {
      router.push({
        name: PAYMENTS_SECTIONS_ROUTES.GEO_MULTI_METHOD_PAGE,
        params: {
          id: multiMethodItem.multiMethodId,
        },
      });
    };

    const getRowClassName = (
      { row: geoPaymentMethod }: { row: GeoPaymentMethodItem | GeoMultiPaymentMethodListItem },
    ): string => {
      const classList: string[] = [];

      if (geoPaymentMethod.type === GeoPaymentMethodTypes.MULTI && !geoPaymentMethod.enabled) {
        classList.push('geo-payment-methods__disabled-row');
      }

      if (geoPaymentMethod.underExperiment) {
        classList.push('geo-payment-methods__experiment-row');
      }

      return classList.join(' ');
    };

    const extendedGeoPaymentMethodList = computed<GeoPaymentMethodWithIdList>(() =>
      geoPaymentMethodList.value.map(geoPaymentMethodItem => {
        const id = 'billingPaymentMethodSettingsId' in geoPaymentMethodItem
          ? geoPaymentMethodItem.billingPaymentMethodSettingsId
          : geoPaymentMethodItem.multiMethodId;

        if (geoPaymentMethodItem.type === GeoPaymentMethodTypes.SINGLE) {
          return {
            ...geoPaymentMethodItem,
            visible: geoPaymentMethodItem.visible ?? true,
            id,
          };
        }

        return { ...geoPaymentMethodItem, id };
      }),
    );

    return {
      icons,
      isGeoPaymentMethodListLoading,
      extendedGeoPaymentMethodList,
      geoPaymentMethodColumnsList,
      GeoPaymentMethodColumnProperties,
      switchPaymentMethod,
      dragSortingTableRef,
      isResortingInProgress,
      requestDeleteConfirmation,
      isDepositColumnsDataVisible,
      isWithdrawColumnsDataVisible,
      GeoPaymentMethodOperation,
      isUpdateGeoPaymentMethodAllowed,
      isUpdateGeoPaymentMethodDepositLimitsAllowed,
      isDeleteGeoPaymentMethodAllowed,
      isPartialUpdateGlobalPaymentMethodAllowed,
      isGeoPaymentMethodFullListUpdateAllowed,
      isActionsColumnVisible,
      isGetGeoPaymentMethodDepositLimitsAllowed,
      isSettingsButtonAllowed,
      getOrganicsAvailabilityData,
      getAvailabilityHoursData,
      getPaymentMethodTagType,
      openMultiMethodPage,
      GeoPaymentMethodTypes,
      GeoDropdownActionCommand,
      getRowClassName,
      updateGeoMultiMethodWithdrawState,
      isGetGeoPaymentMethodExtendedLimitAllowed,
      PaymentMethodExceptionLimitType,
      getGeoDropdownActionCommand,
      isGetGeoPaymentMethodSetAvailabilityLimitAllowed,
    };
  },
});
